<template>
  <div class="nd-page">
    <header class="nd-page-header">
      <div>
        <span id="title" class="page-title">Convênios</span>
        <div class="nd-page-kicker">
          Gerencie operadoras, acompanhe vínculos e mantenha a operação clínica organizada.
        </div>
      </div>

      <div class="button-group">
        <v-btn color="primary" elevation="0" to="/convenios/novo">
          <v-icon left size="18">mdi-plus</v-icon>
          Novo convênio
        </v-btn>
      </div>
    </header>

    <module-stat-grid module="insurances" :items="data" :count="count" />

    <v-card class="nd-section-card">
      <div class="nd-table-toolbar">
        <div>
          <div class="nd-table-toolbar__title">Lista de convênios</div>
          <div class="nd-table-toolbar__subtitle">Busca rápida por nome da operadora</div>
        </div>

        <div class="nd-table-toolbar__actions">
          <v-text-field
            v-model="filter.name"
            class="nd-search-field"
            dense
            outlined hide-details="auto"
            placeholder="Buscar convênio"
            prepend-inner-icon="mdi-magnify"
            @keyup.enter="applyFilter"
          />
          <v-btn outlined color="primary" elevation="0" @click="applyFilter">
            <v-icon left size="18">mdi-filter-outline</v-icon>
            Filtrar
          </v-btn>
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="data"
        :server-items-length="count"
        :loading="loading"
        no-data-text="Nenhum convênio encontrado"
        loading-text="Carregando convênios..."
        class="app-table hover-table"
        :options.sync="options"
        :footer-props="footerOptions"
        @update:options="onTableOptionsChanged"
        @click:row="viewItem"
      >
        <template v-slot:[`item.name`]="{ item }">
          <div class="insurance-name-cell">
            <span class="insurance-name-cell__mark">{{ getInitials(item.name) }}</span>
            <div>
              <strong>{{ item.name }}</strong>
              <small>ID {{ item.id }}</small>
            </div>
          </div>
        </template>

        <template v-slot:[`item.status`]>
          <span class="nd-status-pill">
            <v-icon size="14" color="#A04DC2">mdi-check-circle-outline</v-icon>
            Ativo
          </span>
        </template>

        <template v-slot:[`item.date`]="{ item }">
          {{ formatDate(item.date) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="row-actions" @click.stop>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn icon small v-on="on" @click="viewItem(item)">
                  <v-icon size="20">mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>Editar convênio</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn icon small v-on="on" @click="deleteItem(item)">
                  <v-icon size="20">mdi-trash-can-outline</v-icon>
                </v-btn>
              </template>
              <span>Remover convênio</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import ModuleStatGrid from "@/components/ModuleStatGrid.vue";
import axios from "axios";

export default {
  components: { ModuleStatGrid },
  name: "InsurancesList",

  data: () => ({
    filter: { name: "" },
    headers: [
      { text: "Convênio", value: "name" },
      { text: "Status", value: "status", sortable: false },
      { text: "Atualizado", value: "date" },
      { text: "", value: "actions", sortable: false, align: "end" },
    ],
    data: [],
    loading: false,
    count: 0,
    options: { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] },
    footerOptions: {
      itemsPerPageText: "Itens por página:",
      itemsPerPageOptions: [10, 20, 30, 40, 50],
      pageText: "{0}-{1} de {2}",
    },
  }),

  computed: {
    lastUpdate() {
      if (!this.data.length) return "-";
      return this.formatDate(this.data[0].date);
    },
  },

  methods: {
    onTableOptionsChanged(options) {
      this.options = { ...options };
      this.load();
    },

    applyFilter() {
      this.options = { ...this.options, page: 1 };
      this.load();
    },

    async load() {
      this.loading = true;
      try {
        const { page, itemsPerPage } = this.options;
        const params = { start: (page - 1) * itemsPerPage, limit: itemsPerPage };
        if (this.filter.name) params.search = this.filter.name;

        const response = await axios.get("/insurance", { params });
        this.data = response.data.data || [];
        this.count = response.data.count || 0;
      } catch (error) {
        console.error("Erro ao carregar convênios:", error);
        this.$dialog.notify.error("Erro ao carregar convênios.", { position: "top-right", timeout: 5000 });
      } finally {
        this.loading = false;
      }
    },

    viewItem(item) {
      this.$router.push(`/convenios/${item.id}`);
    },

    async deleteItem(item) {
      const confirm = await this.$dialog.confirm({
        text: `Tem certeza que deseja remover o convênio "${item.name}"?`,
        title: "Confirmar exclusão",
        actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
      });
      if (!confirm) return;
      try {
        this.loading = true;
        await axios.delete(`/insurance/${item.id}`);
        this.$dialog.notify.success("Convênio removido com sucesso!", { position: "top-right", timeout: 3000 });
        await this.load();
      } catch (error) {
        this.$dialog.notify.error("Erro ao remover convênio.", { position: "top-right", timeout: 5000 });
      } finally {
        this.loading = false;
      }
    },

    formatDate(value) {
      return value ? new Date(value).toLocaleDateString("pt-BR") : "-";
    },

    getInitials(name) {
      if (!name) return "C";
      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((item) => item[0])
        .join("")
        .toUpperCase();
    },
  },
};
</script>

<style scoped lang="scss">
.insurance-name-cell {
  align-items: center;
  display: flex;
  gap: 12px;

  &__mark {
    align-items: center;
    background: #f5ecfa;
    border-radius: 999px;
    color: #a04dc2;
    display: inline-flex;
    font-size: 12px;
    font-weight: 800;
    height: 34px;
    justify-content: center;
    width: 34px;
  }

  strong,
  small {
    display: block;
  }

  strong {
    color: #111816;
    font-size: 14px;
    line-height: 1.2;
  }

  small {
    color: #69736d;
    font-size: 12px;
    margin-top: 3px;
  }
}

.row-actions {
  align-items: center;
  display: flex;
  justify-content: flex-end;
}

.nd-stat-card__value--date {
  font-size: 18px;
}
</style>
